<template>
  <v-container fluid class="pa-0 py-8">
    <div :style="{ width: $vuetify.display.xlAndUp ? '85%' : '95%' }" class="mx-auto">
      <!-- Header -->
      <div class="d-flex justify-space-between align-center mb-2 fade-in">
        <div>
          <p class="text-caption text-grey-darken-1 mb-1">Wissensdatenbank</p>
          <h1 class="page-title">Anleitungen</h1>
        </div>
        <button v-if="$can('guides.create')" class="bp" @click="openCreateDialog">
          <v-icon size="16">mdi-plus</v-icon>
          Neue Anleitung
        </button>
      </div>

      <v-divider class="my-6" style="border-color: #eceef2" />

      <!-- Toolbar -->
      <div class="d-flex align-center mb-5 toolbar">
        <div class="search-wrap">
          <v-icon size="18" color="#888" class="search-icon">mdi-magnify</v-icon>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Anleitungen durchsuchen..."
            class="search-input"
          />
          <v-icon
            v-if="searchQuery"
            size="16"
            color="#888"
            class="search-clear"
            @click="searchQuery = ''"
          >mdi-close</v-icon>
        </div>

        <div class="filter-toggle">
          <button
            class="filter-btn"
            :class="{ 'filter-btn-active': filter === 'all' }"
            @click="filter = 'all'"
          >Alle <span class="filter-count">{{ guides.length }}</span></button>
          <button
            class="filter-btn"
            :class="{ 'filter-btn-active': filter === 'with-section' }"
            @click="filter = 'with-section'"
          >Verknüpft</button>
          <button
            class="filter-btn"
            :class="{ 'filter-btn-active': filter === 'without-section' }"
            @click="filter = 'without-section'"
          >Ohne Section</button>
        </div>
      </div>

      <!-- List -->
      <div v-if="loading" class="skel-grid">
        <div v-for="i in 4" :key="`skel-${i}`" class="skel-card"></div>
      </div>

      <div v-else-if="filteredGuides.length === 0" class="empty-state">
        <v-icon size="48" color="#c5cbd3" class="mb-4">mdi-book-open-page-variant-outline</v-icon>
        <h3 class="empty-title">{{ guides.length === 0 ? 'Noch keine Anleitungen' : 'Keine Treffer' }}</h3>
        <p class="empty-desc">
          {{ guides.length === 0
            ? 'Lege deine erste Anleitung an. Du kannst sie optional einer oder mehreren Sections zuweisen.'
            : 'Passe Suchbegriff oder Filter an, um Anleitungen zu finden.' }}
        </p>
        <button v-if="guides.length === 0 && $can('guides.create')" class="bp mt-4" @click="openCreateDialog">
          <v-icon size="16">mdi-plus</v-icon>
          Erste Anleitung anlegen
        </button>
      </div>

      <div v-else class="guides-grid">
        <div
          v-for="g in filteredGuides"
          :key="g.id"
          class="guide-card"
          @click="$can('guides.edit') ? openEditDialog(g) : openViewer(g)"
        >
          <div class="guide-card-head">
            <h3 class="guide-card-title">{{ g.title }}</h3>
            <div v-if="$can('guides.edit') || $can('guides.delete')" class="guide-card-actions" @click.stop>
              <button
                v-if="$can('guides.edit')"
                class="task-icon-btn"
                @click="openEditDialog(g)"
                title="Bearbeiten"
              >
                <v-icon size="14">mdi-pencil-outline</v-icon>
              </button>
              <button
                v-if="$can('guides.delete')"
                class="task-icon-btn task-icon-btn-danger"
                @click="confirmDelete(g)"
                title="Löschen"
              >
                <v-icon size="14">mdi-delete-outline</v-icon>
              </button>
            </div>
          </div>

          <p v-if="contentPreview(g)" class="guide-card-preview">{{ contentPreview(g) }}</p>

          <!-- Sections -->
          <div v-if="g.sections.length > 0" class="guide-sections">
            <span
              v-for="(sec, i) in g.sections.slice(0, 3)"
              :key="sec.id"
              class="guide-section-pill"
              :title="sec.handle"
              @click.stop="goToSection(sec.id)"
            >
              <img v-if="sec.imageUrl" :src="sec.imageUrl" class="guide-section-thumb" @error="onAvatarError" />
              <div v-else class="guide-section-thumb guide-section-thumb-empty">
                <v-icon size="9" color="#c5cbd3">mdi-image-off-outline</v-icon>
              </div>
              {{ sec.title }}
            </span>
            <span v-if="g.sections.length > 3" class="guide-section-pill guide-section-pill-more">
              +{{ g.sections.length - 3 }} weitere
            </span>
          </div>
          <div v-else class="guide-no-section">
            <v-icon size="11" color="#c5cbd3">mdi-link-off</v-icon>
            Nicht verknüpft
          </div>

          <!-- Footer: author + updated -->
          <div class="guide-card-foot">
            <div class="guide-author" v-if="g.author">
              <img v-if="g.author.avatar" :src="g.author.avatar" class="avatar-sm" @error="onAvatarError" />
              <span v-else class="avatar-sm avatar-fallback">{{ initials(g.author) }}</span>
              <span class="guide-author-name">{{ g.author.firstname }} {{ g.author.lastname }}</span>
            </div>
            <span class="guide-updated">Zuletzt aktualisiert: {{ formatDate(g.updatedAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / Edit Dialog -->
    <div v-if="dialog" class="dialog-backdrop" @click.self="!saving && closeDialog()"></div>
    <v-dialog v-model="dialog" max-width="880" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="closeDialog"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon"><v-icon size="22" color="#0a1628">mdi-book-open-page-variant-outline</v-icon></div>
          <div>
            <div class="dh-title">{{ readOnly ? 'Anleitung ansehen' : (editMode ? 'Anleitung bearbeiten' : 'Neue Anleitung') }}</div>
            <div class="dh-sub">
              <template v-if="readOnly">Du hast keine Bearbeitungs-Rechte. Inhalte werden hier nur angezeigt.</template>
              <template v-else>Schreibe einen ausführlichen Leitfaden — formatiert mit Überschriften, Listen, Bildern und Code.</template>
            </div>
          </div>
        </div>

        <div class="db" :class="{ 'db-readonly': readOnly }">
          <!-- Title -->
          <div class="field-block">
            <label class="field-label">Titel *</label>
            <input
              v-model="form.title"
              type="text"
              class="text-input"
              :class="{ 'input-error': submitted && !form.title.trim() }"
              placeholder="z.B. Hero Video Section – Setup-Anleitung"
              :disabled="readOnly"
            />
          </div>

          <!-- Content -->
          <div class="field-block">
            <label class="field-label">Inhalt</label>
            <div class="rich-editor-wrap">
              <QuillEditor
                v-model:content="form.content"
                content-type="html"
                theme="snow"
                placeholder="Schreibe deine Anleitung…"
                :toolbar="quillToolbar"
                :read-only="readOnly"
                style="min-height: 280px"
              />
            </div>
          </div>

          <!-- Section assignments -->
          <div class="field-block">
            <label class="field-label">Verknüpfte Sections <span class="text-grey ml-1">(optional)</span></label>
            <div class="combo-wrap" ref="sectionDropdown">
              <button
                type="button"
                class="iw iw-trigger"
                :class="{ 'iw-f': sectionMenuOpen }"
                :disabled="readOnly"
                @click="readOnly ? null : (sectionMenuOpen = !sectionMenuOpen)"
              >
                <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-vector-intersection</v-icon>
                <span class="ii-val">
                  {{ form.sectionIds.length === 0 ? 'Keine Section ausgewählt' :
                     `${form.sectionIds.length} Section${form.sectionIds.length === 1 ? '' : 's'} ausgewählt` }}
                </span>
                <v-icon size="14" color="#888" :class="{ 'chev-open': sectionMenuOpen }">mdi-chevron-down</v-icon>
              </button>
              <div v-if="sectionMenuOpen" class="combo-menu combo-menu-section">
                <div class="combo-search">
                  <v-icon size="14" color="#888">mdi-magnify</v-icon>
                  <input
                    v-model="sectionSearch"
                    type="text"
                    placeholder="Section suchen..."
                    class="combo-search-input"
                    @click.stop
                  />
                </div>
                <div class="combo-divider"></div>
                <div v-if="filteredSections.length === 0" class="combo-empty">Keine Section gefunden</div>
                <button
                  v-for="sec in filteredSections"
                  :key="sec.id"
                  type="button"
                  class="combo-option combo-option-section"
                  @click="toggleSection(sec.id)"
                >
                  <img v-if="sec.imageUrl" :src="sec.imageUrl" class="combo-section-thumb" @error="onAvatarError" />
                  <div v-else class="combo-section-thumb combo-section-thumb-empty">
                    <v-icon size="13" color="#c5cbd3">mdi-image-off-outline</v-icon>
                  </div>
                  <div class="combo-section-text">
                    <div class="combo-label">{{ sec.title }}</div>
                    <div class="combo-handle">{{ sec.handle }}</div>
                  </div>
                  <v-icon
                    size="16"
                    :color="form.sectionIds.includes(sec.id) ? '#2E7D32' : '#c5cbd3'"
                  >
                    {{ form.sectionIds.includes(sec.id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                  </v-icon>
                </button>
              </div>
            </div>

            <!-- Selected chips -->
            <div v-if="form.sectionIds.length > 0" class="selected-chips">
              <span
                v-for="sid in form.sectionIds"
                :key="sid"
                class="selected-chip"
              >
                <img
                  v-if="sectionsById[sid] && sectionsById[sid].imageUrl"
                  :src="sectionsById[sid].imageUrl"
                  class="selected-chip-thumb"
                  @error="onAvatarError"
                />
                {{ sectionsById[sid] ? sectionsById[sid].title : '#' + sid }}
                <button
                  v-if="!readOnly"
                  type="button"
                  class="selected-chip-remove"
                  @click.stop="toggleSection(sid)"
                >
                  <v-icon size="11">mdi-close</v-icon>
                </button>
              </span>
            </div>
          </div>

          <div v-if="formError" class="alert-error mt-3">
            <v-icon size="14" color="#D32F2F">mdi-alert-circle-outline</v-icon>
            {{ formError }}
          </div>
        </div>

        <div class="dff">
          <button class="bo" @click="closeDialog" :disabled="saving">
            {{ readOnly ? 'Schließen' : 'Abbrechen' }}
          </button>
          <button v-if="!readOnly" class="bp" @click="saveGuide" :disabled="saving">
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">{{ editMode ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
            {{ editMode ? 'Speichern' : 'Anlegen' }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <div v-if="deleteDialog" class="dialog-backdrop" @click.self="!deleting && (deleteDialog = false)"></div>
    <v-dialog v-model="deleteDialog" max-width="440" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="deleteDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(211,47,47,0.1)">
            <v-icon size="22" color="#D32F2F">mdi-delete-outline</v-icon>
          </div>
          <div>
            <div class="dh-title">Anleitung löschen?</div>
            <div class="dh-sub">Diese Aktion kann nicht rückgängig gemacht werden.</div>
          </div>
        </div>
        <div class="db">
          <p class="text-body-2" style="color: #0a1628">
            <strong>{{ deleteTarget?.title }}</strong> wird endgültig entfernt.
            <span v-if="deleteTarget?.sections?.length > 0">
              Verknüpfungen zu {{ deleteTarget.sections.length }} Section(s) werden automatisch gelöst.
            </span>
          </p>
        </div>
        <div class="dff">
          <button class="bo" @click="deleteDialog = false" :disabled="deleting">Abbrechen</button>
          <button class="bp btn-danger" @click="executeDelete" :disabled="deleting">
            <v-progress-circular v-if="deleting" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-delete-outline</v-icon>
            Endgültig löschen
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Toast Stack -->
    <div class="toast-stack">
      <transition-group name="toast">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="toast"
          :class="{ 'toast-error': t.type === 'error' }"
          @click="dismissToast(t.id)"
        >
          <div class="toast-icon" :class="{ 'toast-icon-error': t.type === 'error' }">
            <v-icon size="16" color="white">{{ t.type === 'error' ? 'mdi-alert-circle-outline' : 'mdi-check' }}</v-icon>
          </div>
          <div class="toast-body">
            <div class="toast-title">{{ t.title }}</div>
            <div v-if="t.message" class="toast-message">{{ t.message }}</div>
          </div>
        </div>
      </transition-group>
    </div>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "GuidesOverview",

  data() {
    return {
      guides: [],
      sections: [],
      loading: true,
      saving: false,
      deleting: false,

      searchQuery: "",
      filter: "all", // all | with-section | without-section

      // Dialog
      dialog: false,
      editMode: false,
      readOnly: false,
      submitted: false,
      formError: "",
      form: this.emptyForm(),

      // Section dropdown
      sectionMenuOpen: false,
      sectionSearch: "",

      // Delete
      deleteDialog: false,
      deleteTarget: null,

      // Toast
      toasts: [],
      toastSeq: 0,

      quillToolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link"],
        ["clean"],
      ],
    };
  },

  computed: {
    filteredGuides() {
      const q = this.searchQuery.trim().toLowerCase();
      let list = this.guides;
      if (q) {
        list = list.filter((g) => (g.title || "").toLowerCase().includes(q));
      }
      if (this.filter === "with-section") list = list.filter((g) => g.sections.length > 0);
      else if (this.filter === "without-section") list = list.filter((g) => g.sections.length === 0);
      return list;
    },
    filteredSections() {
      const q = this.sectionSearch.trim().toLowerCase();
      if (!q) return this.sections;
      return this.sections.filter(
        (s) => (s.title || "").toLowerCase().includes(q) || (s.handle || "").toLowerCase().includes(q)
      );
    },
    sectionsById() {
      const map = {};
      for (const s of this.sections) map[s.id] = s;
      return map;
    },
  },

  mounted() {
    this.loadAll();
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },

  methods: {
    emptyForm() {
      return { id: null, title: "", content: "", sectionIds: [] };
    },

    async loadAll() {
      this.loading = true;
      try {
        const [guidesRes, sectionsRes] = await Promise.all([
          axios.get("/api/guides/get"),
          axios.get("/api/sections/get", { params: { includeDrafts: "true" } }),
        ]);
        this.guides = guidesRes.data.data || [];
        this.sections = (sectionsRes.data || []).map((s) => ({
          id: s.id,
          title: s.title,
          handle: s.handle,
          hasImage: !!s.hasImage,
          imageUrl: s.hasImage
            ? `/api/sections/${s.id}/image?v=${new Date(s.updatedAt).getTime()}`
            : null,
        }));
      } catch (e) {
        console.error("Fehler beim Laden:", e);
      } finally {
        this.loading = false;
      }
    },

    handleOutsideClick(e) {
      if (this.sectionMenuOpen) {
        const el = this.$refs.sectionDropdown;
        if (el && !el.contains(e.target)) this.sectionMenuOpen = false;
      }
    },

    openCreateDialog() {
      this.form = this.emptyForm();
      this.editMode = false;
      this.readOnly = false;
      this.submitted = false;
      this.formError = "";
      this.dialog = true;
    },

    openEditDialog(g) {
      this.form = {
        id: g.id,
        title: g.title || "",
        content: g.content || "",
        sectionIds: (g.sections || []).map((s) => s.id),
      };
      this.editMode = true;
      this.readOnly = !this.$can("guides.edit");
      this.submitted = false;
      this.formError = "";
      // Wenn Liste keinen content liefert (Performance), nachladen
      if (!this.form.content) this.loadGuideContent(g.id);
      this.dialog = true;
    },

    openViewer(g) {
      this.openEditDialog(g);
      this.readOnly = true;
    },

    async loadGuideContent(id) {
      try {
        const res = await axios.get("/api/guides/get", { params: { id } });
        if (res.data?.data) {
          this.form.content = res.data.data.content || "";
        }
      } catch (e) {
        console.error("Fehler beim Laden des Contents:", e);
      }
    },

    closeDialog() {
      this.dialog = false;
      this.sectionMenuOpen = false;
      this.sectionSearch = "";
    },

    toggleSection(sid) {
      if (this.readOnly) return;
      const idx = this.form.sectionIds.indexOf(sid);
      if (idx >= 0) this.form.sectionIds.splice(idx, 1);
      else this.form.sectionIds.push(sid);
    },

    async saveGuide() {
      this.submitted = true;
      this.formError = "";
      if (!this.form.title.trim()) {
        this.formError = "Titel ist erforderlich.";
        return;
      }
      this.saving = true;
      try {
        const payload = {
          title: this.form.title.trim(),
          content: this.form.content || null,
          sectionIds: this.form.sectionIds,
        };
        if (this.editMode) {
          payload.id = this.form.id;
          await axios.post("/api/guides/update", payload);
        } else {
          await axios.post("/api/guides/create", payload);
        }
        await this.loadAll();
        this.closeDialog();
        this.showToast({
          type: "success",
          title: this.editMode ? "Anleitung aktualisiert" : "Anleitung angelegt",
          message: this.form.title,
        });
      } catch (e) {
        this.formError = e.response?.data?.message || "Fehler beim Speichern.";
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(g) {
      this.deleteTarget = g;
      this.deleteDialog = true;
    },

    async executeDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        await axios.post("/api/guides/delete", { id: this.deleteTarget.id });
        await this.loadAll();
        this.deleteDialog = false;
        this.showToast({ type: "success", title: "Anleitung gelöscht", message: this.deleteTarget.title });
        this.deleteTarget = null;
      } catch (e) {
        this.showToast({ type: "error", title: "Fehler", message: e.response?.data?.message || "Konnte nicht gelöscht werden" });
      } finally {
        this.deleting = false;
      }
    },

    showToast(toast) {
      const id = ++this.toastSeq;
      this.toasts.push({ id, ...toast });
      setTimeout(() => this.dismissToast(id), toast.type === "error" ? 5000 : 3500);
    },
    dismissToast(id) {
      const idx = this.toasts.findIndex((t) => t.id === id);
      if (idx >= 0) this.toasts.splice(idx, 1);
    },

    onAvatarError(e) { e.target.style.display = "none"; },
    initials(p) {
      return ((p?.firstname?.charAt(0) || "") + (p?.lastname?.charAt(0) || "")) || "?";
    },
    contentPreview(g) {
      // Bevorzugt das vom Backend gelieferte SUBSTRING-Feld (List-Endpoint),
      // fällt sonst auf vollen content zurück (Detail-Endpoint).
      const html = g.contentPreview || g.content || "";
      if (!html) return "";
      const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      return text.length > 220 ? text.substring(0, 220).trimEnd() + "…" : text;
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    goToSection(id) {
      this.$router.push(`/section/${id}`);
    },
  },
};
</script>

<style scoped>
.fade-in { animation: fadeIn 0.4s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }

:root, .toolbar, .guide-card, .modern-dialog { --ease: cubic-bezier(0.32, 0.72, 0, 1); }

.page-title {
  font-size: 26px; font-weight: 800; color: #0a1628;
  letter-spacing: -0.3px; line-height: 1.1; margin: 0;
}

.bp {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; font-family: inherit;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }
.bp.btn-danger { background: #D32F2F; }
.bp.btn-danger:hover:not(:disabled) { background: #b71c1c; }

.bo {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.4; cursor: not-allowed; }

.toolbar { gap: 12px; flex-wrap: wrap; }
.search-wrap {
  position: relative; display: flex; align-items: center;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  padding: 0 12px 0 38px; height: 40px; width: 280px;
  transition: border-color 0.15s;
}
.search-wrap:focus-within { border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.search-icon { position: absolute; left: 12px; }
.search-clear { position: absolute; right: 10px; cursor: pointer; }
.search-input { border: none; outline: none; font-size: 13px; color: #0a1628; width: 100%; background: transparent; font-family: inherit; }

.filter-toggle {
  display: inline-flex; padding: 4px;
  background: white; border: 1px solid #eceef2; border-radius: 10px; height: 40px;
}
.filter-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 0 14px; background: transparent; border: none;
  font-size: 12px; font-weight: 600; color: #888;
  cursor: pointer; border-radius: 6px; transition: all 0.15s; font-family: inherit;
}
.filter-btn:not(.filter-btn-active):hover { color: #0a1628; background: #f0f2f5; }
.filter-btn-active { background: #0a1628; color: white; }
.filter-btn-active:hover { background: #1d3457; color: white; }
.filter-count {
  margin-left: 4px; padding: 0 6px;
  background: rgba(255,255,255,0.18);
  border-radius: 8px; font-size: 10px; font-weight: 700;
}
.filter-btn:not(.filter-btn-active) .filter-count { background: #f0f2f5; color: #888; }

/* Grid */
.guides-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 14px;
}

/* Card */
.guide-card {
  background: white;
  border: 1px solid #eceef2;
  border-radius: 12px;
  padding: 16px 18px 14px;
  cursor: pointer;
  transition: box-shadow 0.22s var(--ease), border-color 0.22s var(--ease);
  display: flex; flex-direction: column; gap: 10px;
  min-height: 200px;
}
.guide-card:hover {
  border-color: #d4d7de;
  box-shadow: 0 1px 2px rgba(10,22,40,0.04), 0 4px 14px rgba(10,22,40,0.05);
}
.guide-card-head {
  display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;
}
.guide-card-title {
  font-size: 15px; font-weight: 700; color: #0a1628;
  line-height: 1.3; letter-spacing: -0.15px; margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.guide-card-actions {
  display: flex; gap: 4px;
  opacity: 0; transition: opacity 0.18s var(--ease);
}
.guide-card:hover .guide-card-actions { opacity: 1; }
.task-icon-btn {
  width: 26px; height: 26px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 5px;
  color: #9ca3af; cursor: pointer; transition: all 0.15s;
}
.task-icon-btn:hover { background: #f0f2f5; color: #0a1628; }
.task-icon-btn-danger:hover { background: rgba(211,47,47,0.08); color: #D32F2F; }

.guide-card-preview {
  font-size: 11.5px; color: #9ca3af; line-height: 1.45; margin: 0;
  font-weight: 400;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.guide-sections {
  display: flex; flex-wrap: wrap; gap: 5px;
  margin-top: auto;
}
.guide-section-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 8px;
  background: rgba(91,91,214,0.08);
  border-radius: 5px;
  font-size: 11px; font-weight: 500; color: #5B5BD6;
  cursor: pointer; transition: background 0.15s; max-width: 100%;
}
.guide-section-pill:hover { background: rgba(91,91,214,0.14); }
.guide-section-pill-more {
  background: #f0f2f5; color: #6b7280; cursor: default;
}
.guide-section-thumb {
  width: 14px; height: 14px; border-radius: 3px; object-fit: cover; flex-shrink: 0;
  background: #eceef2;
}
.guide-section-thumb-empty { display: inline-flex; align-items: center; justify-content: center; }

.guide-no-section {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: #c5cbd3; font-style: italic;
  margin-top: auto;
}

.guide-card-foot {
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 10px; border-top: 1px solid #f0f2f5;
  gap: 8px;
}
.guide-author { display: inline-flex; align-items: center; gap: 7px; min-width: 0; }
.avatar-sm {
  width: 22px; height: 22px; border-radius: 50%; object-fit: cover; flex-shrink: 0;
  border: 1.5px solid white; box-shadow: 0 0 0 1px #eceef2;
}
.avatar-fallback {
  background: #0a1628; color: white;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 700;
}
.guide-author-name {
  font-size: 11px; font-weight: 500; color: #0a1628;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.guide-updated {
  font-size: 10.5px; color: #9ca3af; white-space: nowrap;
}

/* Empty + Skeleton */
.empty-state {
  background: white; border: 1px solid #eceef2; border-radius: 16px;
  padding: 64px 32px; text-align: center;
  display: flex; flex-direction: column; align-items: center;
}
.empty-title { font-size: 16px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.empty-desc { font-size: 13px; color: #888; max-width: 480px; margin: 0 auto; }

.skel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 14px;
}
.skel-card {
  height: 200px; border-radius: 12px;
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite;
}
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

/* Dialog */
.dialog-backdrop {
  position: fixed; inset: 0; background: rgba(10,22,40,0.4); z-index: 1000;
  animation: fadeBg 0.15s ease;
}
@keyframes fadeBg { from { opacity: 0; } to { opacity: 1; } }
.modern-dialog {
  background: white !important;
  position: relative;
  padding: 0; overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 88vh;
}
.close-btn {
  position: absolute; top: 16px; right: 16px;
  width: 32px; height: 32px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px;
  color: #888; cursor: pointer; z-index: 1;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; }
.dh {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 24px 28px 8px;
}
.dh-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(10,22,40,0.06);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.dh-title { font-size: 18px; font-weight: 800; color: #0a1628; }
.dh-sub { font-size: 13px; color: #888; margin-top: 2px; }
.db {
  padding: 16px 28px;
  flex: 1 1 auto; overflow-y: auto; min-height: 0;
}
.db-readonly button:not(.combo-clear),
.db-readonly .iw-trigger,
.db-readonly .text-input { pointer-events: none; }
.db-readonly .text-input { background: #fafbfc; color: #4b5563; }
.db-readonly :deep(.ql-toolbar) { display: none !important; }
.db-readonly :deep(.ql-container) { border-top: none !important; }
.db-readonly :deep(.ql-editor) { background: #fafbfc; color: #4b5563; }

.dff {
  display: flex; gap: 10px; padding: 14px 28px 18px;
  border-top: 1px solid #eceef2;
  background: white; flex: 0 0 auto;
  box-shadow: 0 -4px 12px rgba(10, 22, 40, 0.04);
}
.dff button { flex: 1; }

.field-block { margin-bottom: 14px; }
.field-label { display: block; font-size: 12px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.text-input {
  width: 100%; padding: 10px 12px;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  font-size: 13px; color: #0a1628; font-family: inherit;
  transition: border-color 0.15s;
}
.text-input:focus { outline: none; border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.text-input.input-error { border-color: #D32F2F; }
.alert-error {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 12px; background: rgba(211,47,47,0.05); color: #D32F2F;
  border: 1px solid rgba(211,47,47,0.2); border-radius: 8px;
  font-size: 12px; font-weight: 500;
}

/* Quill Editor */
.rich-editor-wrap {
  border: 1px solid #eceef2;
  border-radius: 10px;
  overflow: hidden;
  background: white;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.rich-editor-wrap:focus-within {
  border-color: #0a1628;
  box-shadow: 0 0 0 3px rgba(10,22,40,0.04);
}
.rich-editor-wrap :deep(.ql-toolbar) {
  border: none !important;
  border-bottom: 1px solid #eceef2 !important;
  background: #fafbfc;
  padding: 6px 8px;
}
.rich-editor-wrap :deep(.ql-container) {
  border: none !important;
  font-family: inherit;
  font-size: 13px;
}
.rich-editor-wrap :deep(.ql-editor) {
  min-height: 240px; color: #0a1628;
  padding: 12px 14px; line-height: 1.55;
}
.rich-editor-wrap :deep(.ql-editor.ql-blank::before) {
  color: #c5cbd3; font-style: normal; font-size: 13px;
  left: 14px; right: 14px;
}
.rich-editor-wrap :deep(.ql-editor h1) { font-size: 22px; font-weight: 800; color: #0a1628; margin: 0.4em 0; }
.rich-editor-wrap :deep(.ql-editor h2) { font-size: 18px; font-weight: 700; color: #0a1628; margin: 0.4em 0; }
.rich-editor-wrap :deep(.ql-editor h3) { font-size: 16px; font-weight: 700; color: #0a1628; margin: 0.4em 0; }
.rich-editor-wrap :deep(.ql-editor a) { color: #5B5BD6; }

/* Section Combo */
.combo-wrap { position: relative; }
.iw-trigger {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 10px 12px;
  background: white !important; border: 1px solid #eceef2; border-radius: 10px;
  font-size: 13px; color: #0a1628; font-family: inherit; cursor: pointer;
  transition: border-color 0.15s; text-align: left;
}
.iw-trigger:hover { border-color: #c5cbd3; }
.iw-trigger.iw-f { border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.ii-val { flex: 1; font-size: 13px; color: #0a1628; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chev-open { transform: rotate(180deg); transition: transform 0.2s; }

.combo-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 320px; overflow-y: auto;
  padding: 6px;
}
.combo-search {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 8px; background: #fafbfc; border-radius: 8px;
  position: sticky; top: 0; z-index: 1;
}
.combo-search-input {
  flex: 1; border: none; background: transparent;
  font-size: 13px; color: #0a1628; outline: none; font-family: inherit;
}
.combo-divider { height: 1px; background: #eceef2; margin: 4px 0; }
.combo-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 6px 8px;
  background: transparent; border: none; border-radius: 7px; cursor: pointer;
  transition: background 0.1s; text-align: left; font-family: inherit;
  font-size: 13px;
}
.combo-option:hover { background: #fafbfc; }
.combo-section-thumb {
  width: 32px; height: 32px; border-radius: 6px; object-fit: cover; flex-shrink: 0;
  background: #f0f2f5;
}
.combo-section-thumb-empty { display: inline-flex; align-items: center; justify-content: center; }
.combo-section-text { flex: 1; min-width: 0; }
.combo-label { font-size: 13px; color: #0a1628; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.combo-handle {
  font-size: 10px; color: #888;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px;
}
.combo-empty { padding: 12px 14px; font-size: 13px; color: #999; text-align: center; }

.selected-chips {
  display: flex; flex-wrap: wrap; gap: 5px;
  margin-top: 8px;
}
.selected-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 4px 3px 4px;
  background: rgba(91,91,214,0.08);
  border-radius: 5px;
  font-size: 11px; font-weight: 500; color: #5B5BD6;
  max-width: 100%;
}
.selected-chip-thumb {
  width: 14px; height: 14px; border-radius: 3px; object-fit: cover; flex-shrink: 0;
  background: #eceef2;
}
.selected-chip-remove {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; padding: 0; margin-left: 2px;
  background: transparent; border: none; border-radius: 4px;
  color: rgba(91,91,214,0.6); cursor: pointer;
}
.selected-chip-remove:hover { background: rgba(91,91,214,0.18); color: #5B5BD6; }

/* Toast */
.toast-stack {
  position: fixed; bottom: 22px; right: 22px;
  display: flex; flex-direction: column; gap: 8px;
  z-index: 2000; pointer-events: none;
}
.toast {
  display: flex; align-items: flex-start; gap: 11px;
  min-width: 300px; max-width: 380px;
  padding: 12px 14px;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 1px 2px rgba(10,22,40,0.04), 0 8px 24px rgba(10,22,40,0.10);
  cursor: pointer; pointer-events: auto;
}
.toast-icon {
  width: 28px; height: 28px; flex-shrink: 0;
  border-radius: 7px; background: #2E7D32;
  display: flex; align-items: center; justify-content: center;
}
.toast-icon-error { background: #D32F2F; }
.toast-body { flex: 1; min-width: 0; }
.toast-title { font-size: 13px; font-weight: 700; color: #0a1628; }
.toast-message { font-size: 11px; color: #6b7280; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.toast-enter-active { transition: opacity 0.24s var(--ease), transform 0.32s var(--ease); }
.toast-leave-active { transition: opacity 0.18s ease-in, transform 0.18s ease-in; }
.toast-enter-from { opacity: 0; transform: translateX(20px); }
.toast-leave-to   { opacity: 0; transform: translateX(20px); }
.toast-move { transition: transform 0.32s var(--ease); }
</style>
